import axios from "axios";
import { NOMINATIM_URL, ONEMAP_AUTH_URL } from "../config";

// Token storage keys
const ONEMAP_TOKEN_KEY = "onemap_access_token";
const ONEMAP_TOKEN_EXPIRY_KEY = "onemap_token_expiry";

// Function to get OneMap auth token
const getOneMapAuthToken = async () => {
  try {
    const response = await axios.post(ONEMAP_AUTH_URL, {
      email: process.env.REACT_APP_ONEMAP_EMAIL || "",
      password: process.env.REACT_APP_ONEMAP_PASSWORD || ""
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.data && response.data.access_token) {
      const { access_token, expiry_timestamp } = response.data;
      
      // Store token and expiry in session storage
      sessionStorage.setItem(ONEMAP_TOKEN_KEY, access_token);
      sessionStorage.setItem(ONEMAP_TOKEN_EXPIRY_KEY, expiry_timestamp);
      
      return access_token;
    }
    
    throw new Error("Failed to get access token from response");
  } catch (error) {
    console.error("Error getting OneMap auth token:", error);
    throw error;
  }
};

// Function to check if token is valid and not expired
const isTokenValid = () => {
  const token = sessionStorage.getItem(ONEMAP_TOKEN_KEY);
  const expiry = sessionStorage.getItem(ONEMAP_TOKEN_EXPIRY_KEY);
  
  if (!token || !expiry) {
    return false;
  }
  
  // Check if token is expired (with 5 minute buffer)
  const currentTime = Math.floor(Date.now() / 1000);
  const expiryTime = parseInt(expiry);
  const bufferTime = 300; // 5 minutes
  
  return currentTime < (expiryTime - bufferTime);
};

// Function to get valid token (from storage or fetch new one)
const getValidToken = async () => {
  if (isTokenValid()) {
    return sessionStorage.getItem(ONEMAP_TOKEN_KEY);
  }
  
  // Token is invalid or expired, get new one
  return await getOneMapAuthToken();
};

// Updated function to get lat/lng with authentication
export const getLatLngFromAddrs = async (query = "") => {
  try {
    // Get valid auth token
    const authToken = await getValidToken();
    
    // Make the API call with Bearer token
    const response = await axios.get(
      NOMINATIM_URL + query + "&returnGeom=Y&getAddrDetails=Y",
      {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      }
    );
    
    return response;
  } catch (error) {
    console.error("Error in getLatLngFromAddrs:", error);
    
    // If it's an auth error, try to refresh token once
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      try {
        console.log("Auth error detected, refreshing token...");
        
        // Clear stored token and get fresh one
        sessionStorage.removeItem(ONEMAP_TOKEN_KEY);
        sessionStorage.removeItem(ONEMAP_TOKEN_EXPIRY_KEY);
        
        const newAuthToken = await getOneMapAuthToken();
        
        // Retry the API call with new token
        const retryResponse = await axios.get(
          NOMINATIM_URL + query + "&returnGeom=Y&getAddrDetails=Y",
          {
            headers: {
              'Authorization': `Bearer ${newAuthToken}`
            }
          }
        );
        
        return retryResponse;
      } catch (retryError) {
        console.error("Retry failed:", retryError);
        return {
          data: { message: "Authentication failed. Please try after sometime." }
        };
      }
    }
    
    // For other errors, return generic error message
    return {
      data: { message: "Some error occurred. Please try after sometime." }
    };
  }
};

// Export both functions for potential direct use
export { getOneMapAuthToken, getValidToken };

// eslint-disable-next-line import/no-anonymous-default-export
export default { getLatLngFromAddrs, getOneMapAuthToken, getValidToken };
